import { ROLE_LABELS, type AuditAction, type AuditEntity, type UserRole } from '@afiliados/shared';
import { IMPORT_STATUS_LABEL, fmtDateTime } from '@/lib/format';

export const ACTION_LABEL: Record<AuditAction, string> = {
  create: 'Criação',
  update: 'Edição',
  delete: 'Exclusão',
  import: 'Importação',
  merge: 'União',
  sync: 'Sincronização',
  login: 'Login',
  login_failed: 'Login falhou',
  logout: 'Logout',
  account_locked: 'Conta bloqueada',
  token_reuse: 'Reuso de sessão',
  password_change: 'Troca de senha',
  password_reset: 'Senha redefinida',
  sessions_revoked: 'Sessões encerradas',
};

export const ACTION_VARIANT: Record<AuditAction, string> = {
  create: 'bg-success/15 text-green-700 dark:text-green-300',
  update: 'bg-warning/20 text-amber-700 dark:text-amber-300',
  delete: 'bg-destructive/10 text-destructive',
  import: 'bg-info/15 text-info',
  merge: 'bg-info/15 text-info',
  sync: 'bg-secondary text-secondary-foreground',
  login: 'bg-secondary text-secondary-foreground',
  login_failed: 'bg-destructive/10 text-destructive',
  logout: 'bg-muted text-muted-foreground',
  account_locked: 'bg-destructive/15 text-destructive',
  token_reuse: 'bg-destructive/15 text-destructive',
  password_change: 'bg-primary/15 text-primary',
  password_reset: 'bg-primary/15 text-primary',
  sessions_revoked: 'bg-muted text-muted-foreground',
};

export const ENTITY_LABEL: Record<AuditEntity, string> = {
  auth: 'Autenticação',
  user: 'Usuário',
  role: 'Perfil',
  affiliate: 'Afiliado',
  product: 'Produto',
  import: 'Importação',
  settings: 'Configurações',
};

export const FIELD_LABEL: Record<string, string> = {
  name: 'Nome',
  email: 'E-mail',
  role: 'Perfil',
  active: 'Ativo',
  mustChangePassword: 'Troca de senha pendente',
  password: 'Senha',
  permissions: 'Permissões',
  reason: 'Motivo',
  lockedUntil: 'Bloqueado até',
  sessions: 'Sessões',
  sessionsRevoked: 'Sessões encerradas',
  ip: 'IP',
  notes: 'Observações',
  period: 'Período',
  rows: 'Linhas',
  grossSales: 'Faturamento bruto',
  netSales: 'Faturamento líquido',
  superseded: 'Substituiu',
  affiliatesCreated: 'Afiliados novos',
  status: 'Status',
  mergedAffiliate: 'Afiliado unido',
  mergedId: 'Id unido',
  rowsMoved: 'Linhas movidas',
  aliases: 'Nomes',
  companyName: 'Empresa',
  timezone: 'Fuso horário',
  mailboxEnabled: 'Leitura automática',
  mailboxHost: 'Servidor IMAP',
  mailboxPort: 'Porta',
  mailboxSecure: 'SSL',
  mailboxUser: 'Usuário',
  mailboxPassword: 'Senha da caixa',
  mailboxFolder: 'Pasta',
  mailboxSenderFilter: 'Filtro de remetente',
  mailboxSubjectFilter: 'Filtro de assunto',
  mailboxIntervalMin: 'Intervalo (min)',
  mailboxMarkSeen: 'Marcar como lida',
  mailboxSince: 'Ignorar antes de',
  cursor: 'Cursor',
  trigger: 'Gatilho',
  file: 'Arquivo',
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

/** Valores com rótulo próprio (enums, booleanos, datas), por campo. */
export function formatValue(field: string, v: unknown): string {
  if (v === null || v === undefined || v === '') return '∅';
  if (typeof v === 'boolean') {
    if (field === 'active') return v ? 'Ativo' : 'Inativo';
    return v ? 'Sim' : 'Não';
  }
  if (typeof v === 'number') return String(v);
  if (Array.isArray(v))
    return v.length ? v.map((x) => formatValue(field, x)).join(', ') : '(vazio)';
  if (typeof v === 'object') return JSON.stringify(v);
  const s = String(v);
  if (field === 'role' && s in ROLE_LABELS) return ROLE_LABELS[s as UserRole];
  if (field === 'status' && s in IMPORT_STATUS_LABEL)
    return IMPORT_STATUS_LABEL[s as keyof typeof IMPORT_STATUS_LABEL];
  if (ISO_DATE.test(s)) return fmtDateTime(s);
  return s;
}
